import React from 'react'
import * as THREE from 'three'
import { RigidBody, CuboidCollider } from '@react-three/rapier'
import { isPhysicsDisabled } from '@utils/debug'

/**
 * Ground Component
 *
 * Provides the ground plane and visual grid for brick placement.
 * Features:
 * - Large plane mesh that receives shadows
 * - GridHelper with 0.8 unit spacing (brick module size)
 * - Static RigidBody with collider for physics interactions (when physics enabled)
 * - Material properties matching the original embedded ground
 * - Task 4.1.6.1: Conditional physics rendering based on ?noPhysics=true flag
 *
 * @component
 */
export const Ground: React.FC = () => {
  const physicsDisabled = isPhysicsDisabled() // Task 4.1.6.1

  return (
    <>
      {/* Ground Plane and Physics - Task 4.1.6.1: Conditionally disabled */}
      {physicsDisabled ? (
        // No physics mode: Just render ground mesh without RigidBody
        <mesh
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, 0, 0]}
          receiveShadow
          name="ground"
        >
          <planeGeometry args={[100, 100]} />
          <meshStandardMaterial
            color="#2a2a2a"
            roughness={0.8}
            metalness={0.0}
          />
        </mesh>
      ) : (
        // Normal mode: Physics enabled with RigidBody
        <RigidBody type="fixed" colliders={false}>
          {/* Thin box collider for ground - more stable than plane collider */}
          <CuboidCollider args={[50, 0.1, 50]} position={[0, -0.1, 0]} />

          {/* Ground Plane - receives shadows from placed bricks */}
          <mesh
            rotation={[-Math.PI / 2, 0, 0]}
            position={[0, 0, 0]}
            receiveShadow
            name="ground"
          >
            <planeGeometry args={[100, 100]} />
            <meshStandardMaterial
              color="#2a2a2a"
              roughness={0.8}
              metalness={0.0}
            />
          </mesh>
        </RigidBody>
      )}

      {/* GridHelper for visual alignment feedback */}
      {/* Using 0.8 unit spacing to match brick module size */}
      <primitive
        object={
          new THREE.GridHelper(
            100, // size of the grid
            125, // number of divisions (100 / 0.8 = 125)
            '#444444', // center line color
            '#333333' // grid line color
          )
        }
        position={[0, 0.01, 0]} // Slightly above ground to prevent z-fighting
      />
    </>
  )
}
